import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button, Form, Alert, Modal } from "react-bootstrap";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { updateSessionById, uploadImage, fetchPracticeTemplates } from "../../api";
import ReactQuill from "react-quill";
import ImageAnnotator from "./EditImages";
import { FaMicrophone, FaStop } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import "./EditSession.css";

// --- Voice recorder hook (modal-driven) ---
const useVoiceRecorder = () => {
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const start = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) return;
    resetTranscript();
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en-US",
    });
  };
  const stop = () => {
    SpeechRecognition.stopListening();
    return (transcript || "").trim();
  };
  const clear = () => resetTranscript();
  return { listening, transcript, start, stop, clear };
};

// --- Recorder Modal ---
const RecorderModal = ({ show, onClose, onDone, vr }) => {
  useEffect(() => {
    if (show) vr.clear();
  }, [show]); // clear transcript on open

  const handleStart = () => vr.start();
  const handleStop = () => vr.stop();
  const handleUse = () => {
    const text = (vr.transcript || "").trim();
    if (vr.listening) vr.stop();
    onDone?.(text);
    onClose?.();
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Voice Recorder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center gap-3">
          <div className={`soundwave ${vr.listening ? "live" : ""}`}>
            <span></span><span></span><span></span><span></span><span></span>
          </div>
          <div className="w-100">
            <Form.Label className="fw-semibold">Live transcript</Form.Label>
            <div
              aria-live="polite"
              style={{
                minHeight: 90,
                border: "1px solid rgba(0,0,0,.1)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                background: "#fafafa",
                whiteSpace: "pre-wrap",
              }}
            >
              {vr.transcript || <span className="text-muted">Say something…</span>}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {!vr.listening ? (
            <Button variant="outline-primary" onClick={handleStart}>
              <FaMicrophone className="me-2" /> Start
            </Button>
          ) : (
            <Button variant="danger" onClick={handleStop}>
              <FaStop className="me-2" /> Stop
            </Button>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleUse} disabled={!vr.transcript?.trim()}>
            Use Transcript
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export const UpdateSession = ({
  sessionId,
  patientId,
  appointmentId,
  serviceName,
  appointmentStart,
  appointmentStatus,
  sections = {},
  notepadContentR = "",
  notepadContentType = "",
  templateId,
  practiceId,
  resourceId,
  activeSection,
  onSaved,
  mediaFilesR = [],
  inline = false,
}) => {
  const [notes, setNotes] = useState([]);
  const [notepadContent, setNotepadContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateId || "");
  const quillRef = useRef(null);
  const annotatorRefs = useRef([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false);
  const [mediaImages, setMediaImages] = useState([]); // strings (dataURL or http)
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camError, setCamError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const loadedNotepadRef = useRef(false);
  const initialTemplateIdRef = useRef(templateId || "");
  const Navigate = useNavigate();

  const safeHtml = (v) => (typeof v === "string" ? v : v ? String(v) : "");

  // shared normalizer
  const normalizeNotes = (incoming) => {
    const arr = Array.isArray(incoming?.sections)
      ? incoming.sections
      : Array.isArray(incoming)
      ? incoming
      : Array.isArray(incoming?.notes)
      ? incoming.notes
      : [];

    return arr.map((n) =>
      n.sessionsFieldType === "multiselect"
        ? {
            ...n,
            sessionsSectionNotes: {
              availableOptions: Array.isArray(n.sessionsSectionNotes?.availableOptions)
                ? n.sessionsSectionNotes.availableOptions
                : Array.isArray(n.sessionsSectionNotes)
                ? n.sessionsSectionNotes
                : [],
              selectedOptions: Array.isArray(n.sessionsSectionNotes?.selectedOptions)
                ? n.sessionsSectionNotes.selectedOptions
                : [],
            },
          }
        : {
            ...n,
            sessionsSectionNotes:
              typeof n.sessionsSectionNotes === "string"
                ? n.sessionsSectionNotes
                : (n.sessionsSectionNotes ?? "") + "",
          }
    );
  };

  // media init/merge
  useEffect(() => {
    const urls = (mediaFilesR || [])
      .map((f) => (typeof f === "string" ? f : f?.fileUrl))
      .filter(Boolean);

    if (!urls.length) return;
    setMediaImages((prev) => {
      if (!prev.length) return urls;
      const seen = new Set(urls);
      return [...urls, ...prev.filter((u) => !seen.has(u))];
    });
  }, [sessionId, mediaFilesR]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async () => {
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamError(null);
    } catch (e) {
      setCamError(e.message || "Camera access failed");
    }
  };

  useEffect(() => {
    if (activeSection === "apptMedia") startStream();
    else stopStream();
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, facingMode]);

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setMediaImages((prev) => [dataUrl, ...prev]);
  };

  const removePhoto = (idx) =>
    setMediaImages((prev) => prev.filter((_, i) => i !== idx));

  const flipCamera = () =>
    setFacingMode((m) => (m === "environment" ? "user" : "environment"));

  // Recorder wiring
  const vr = useVoiceRecorder();
  const [recOpen, setRecOpen] = useState(false);
  const [recTarget, setRecTarget] = useState(null); // { type:'template'|'notepad', index? }

  const openRecorderForTemplate = (index) => {
    setRecTarget({ type: "template", index });
    setRecOpen(true);
  };
  const openRecorderForNotepad = () => {
    setRecTarget({ type: "notepad" });
    setRecOpen(true);
  };
  const closeRecorder = () => {
    if (vr.listening) vr.stop();
    setRecOpen(false);
    setRecTarget(null);
  };

  const insertToQuill = (value) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const end = quill.getLength();
    const needsSpace = end > 1 && /\S/.test(quill.getText(end - 2, 1));
    const text = (needsSpace ? " " : "") + (value || "");
    quill.insertText(end, text, "user");
    quill.setSelection(end + text.length, 0, "user");
    setTimeout(() => setNotepadContent(quill.root.innerHTML), 0);
  };

  const applyTranscriptToTarget = (text) => {
    const cleaned = (text || "").trim();
    if (!cleaned) return;
    if (recTarget?.type === "template" && Number.isInteger(recTarget.index)) {
      setNotes((prev) => {
        const updated = [...prev];
        const idx = recTarget.index;
        const raw = updated[idx]?.sessionsSectionNotes;
        const current = typeof raw === "string" ? raw : (raw ?? "") + "";
        updated[idx] = {
          ...updated[idx],
          sessionsSectionNotes: current ? `${current} ${cleaned}` : cleaned,
        };
        return updated;
      });
    } else if (recTarget?.type === "notepad") {
      insertToQuill(cleaned);
    }
  };

  useEffect(
    () => () => {
      if (vr.listening) vr.stop();
    },
    [vr.listening]
  );

  useEffect(() => {
    annotatorRefs.current = annotatorRefs.current.slice(0, notes.length);
  }, [notes.length]);

  const commitOneImage = (idx) => {
    const ref = annotatorRefs.current[idx];
    if (ref && typeof ref.mergeImage === "function") {
      try {
        const merged = ref.mergeImage();
        if (merged && typeof merged === "string") {
          setNotes((prev) => {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], sessionsImageURL: merged };
            return copy;
          });
        }
      } catch (e) {
        console.warn("mergeImage failed for index", idx, e);
      }
    }
  };

  const commitAllImages = () => {
    let changed = false;
    const copy = [...notes];
    copy.forEach((note, idx) => {
      if (note?.sessionsFieldType === "image") {
        const ref = annotatorRefs.current[idx];
        if (ref && typeof ref.mergeImage === "function") {
          try {
            const merged = ref.mergeImage();
            if (merged && typeof merged === "string" && merged !== note.sessionsImageURL) {
              copy[idx] = { ...note, sessionsImageURL: merged };
              changed = true;
            }
          } catch (e) {
            console.warn("mergeImage failed for index", idx, e);
          }
        }
      }
    });
    if (changed) setNotes(copy);
  };

  useEffect(() => {
    if (activeSection !== "apptTemplate") commitAllImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Templates fetch
  useEffect(() => {
    const initializeData = async () => {
      const token = sessionStorage.getItem("user");
      if (!token) return;
      try {
        const decoded = jwtDecode(token);
        const userPractices = decoded.practice;
        if (!userPractices || userPractices.length === 0) return;

        const templatesData = await fetchPracticeTemplates(decoded._id);
        const filteredTemplates = templatesData.filter((template) =>
          template.templatePracticeId.some((p) => userPractices.includes(p))
        );

        const initialTemplates = filteredTemplates.filter((t) => t.templateInitial === "initial");
        const followUpTemplates = filteredTemplates.filter((t) => t.templateInitial === "followup");

        setDropdownOptions([
          { label: "Initial Assessment", options: initialTemplates },
          { label: "Follow Up", options: followUpTemplates },
        ]);
        setTemplates(filteredTemplates);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    initializeData();
  }, []);

  // ✅ Rehydrate template notes whenever the session/sections change
  useEffect(() => {
    const normalized = normalizeNotes(sections);
    setNotes(normalized);
    setSelectedTemplateId((prev) => prev || templateId || "");
    initialTemplateIdRef.current = templateId || "";
    annotatorRefs.current = Array(normalized.length).fill(null);
  }, [sessionId, sections, templateId]);

  // Notepad content: once per sessionId
  useEffect(() => {
    loadedNotepadRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    if (loadedNotepadRef.current) return;
    const content =
      safeHtml(notepadContentR) ??
      safeHtml(sections?.notepadContent) ??
      safeHtml(sections?.sessionsNotepadContent) ??
      "";
    setNotepadContent(content);
    loadedNotepadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Template select change
  const handleTemplateChange = (newId) => {
    commitAllImages();
    setSelectedTemplateId(newId);
    const sel = templates.find((t) => t._id === newId);
    if (sel && Array.isArray(sel.notes)) {
      const formatted = sel.notes.map((note) =>
        note.sessionsFieldType === "multiselect"
          ? {
              ...note,
              sessionsSectionNotes: {
                availableOptions: Array.isArray(note.sessionsSectionNotes)
                  ? note.sessionsSectionNotes
                  : [],
                selectedOptions: [],
              },
            }
          : {
              ...note,
              sessionsSectionNotes:
                typeof note.sessionsSectionNotes === "string" ? note.sessionsSectionNotes : "",
            }
      );
      setNotes(formatted);
      annotatorRefs.current = Array(formatted.length).fill(null);
    }
  };

  // Helpers used in render
  const getFieldValue = (note) => {
    if (!note?.sessionsSectionNotes) return "";
    if (typeof note.sessionsSectionNotes === "object") return "";
    return note.sessionsSectionNotes;
  };
  const handleTemplateFieldChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].sessionsSectionNotes = value;
      return updated;
    });
  };
  const handleMultiselectChange = (index, option) => {
    setNotes((prev) =>
      prev.map((note, i) => {
        if (i !== index) return note;
        const current = note.sessionsSectionNotes?.selectedOptions || [];
        const selected = current.includes(option)
          ? current.filter((x) => x !== option)
          : [...current, option];
        return {
          ...note,
          sessionsSectionNotes: {
            ...note.sessionsSectionNotes,
            selectedOptions: selected,
          },
        };
      })
    );
  };

  // Save
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      commitAllImages();

      // process image fields from notes
      const processedNotes = [];
      for (let i = 0; i < (notes || []).length; i++) {
        const n = notes[i];
        if (n.sessionsFieldType !== "image") {
          processedNotes.push(n);
          continue;
        }
        const url = typeof n.sessionsImageURL === "string" ? n.sessionsImageURL : "";
        if (url && url.startsWith("data:")) {
          const blob = await fetch(url).then((r) => r.blob());
          const fd = new FormData();
          fd.append("image", blob);
          fd.append("x-image-caption", "Session Image Upload");
          fd.append("patientId", patientId);
          fd.append("appointmentId", appointmentId);
          const up = await uploadImage(fd);
          processedNotes.push({ ...n, sessionsImageURL: up.imageUrl });
        } else {
          processedNotes.push(n);
        }
      }

      // media
      const finalMediaFiles = [];
      for (let idx = 0; idx < (mediaImages || []).length; idx++) {
        const m = mediaImages[idx];
        if (typeof m !== "string") continue;
        if (m.startsWith("data:")) {
          const blob = await fetch(m).then((r) => r.blob());
          const fd = new FormData();
          fd.append("image", blob);
          fd.append("x-image-caption", `Media Photo ${idx + 1}`);
          fd.append("patientId", patientId);
          fd.append("appointmentId", appointmentId);
          const up = await uploadImage(fd);
          finalMediaFiles.push({
            fileName: `media-photo-${idx + 1}`,
            fileUrl: up.imageUrl,
            fileType: "image",
            uploadedAt: new Date().toISOString(),
            description: `Captured photo ${idx + 1}`,
          });
        } else if (/^https?:\/\//.test(m)) {
          finalMediaFiles.push({
            fileName: `media-photo-${idx + 1}`,
            fileUrl: m,
            fileType: "image",
            uploadedAt: new Date().toISOString(),
            description: `Captured photo ${idx + 1}`,
          });
        }
      }
      const seen = new Set();
      const mediaFiles = finalMediaFiles.filter((f) => {
        const u = f.fileUrl;
        if (!u || seen.has(u)) return false;
        seen.add(u);
        return true;
      });

      const includeTemplateChange =
        selectedTemplateId && selectedTemplateId !== initialTemplateIdRef.current;

      const payload = {
        sessionsPatientId: patientId,
        sessionsPracticeId: practiceId,
        sessionsAppointmentId: appointmentId,
        sessionsServiceName: serviceName,
        sessionsResourceId: resourceId,
        sessionsAppointmentStart: appointmentStart,
        ...(includeTemplateChange ? { sessionsTemplateId: selectedTemplateId } : {}),
        // Always send sections to avoid wiping out template on notepad-only edits
        sections: processedNotes,
        ...(notepadContent?.trim() ? { notepadContent, notepadContentType: "html" } : {}),
        ...(mediaFiles.length ? { mediaFiles } : {}),
      };

      await updateSessionById(sessionId, payload);

      initialTemplateIdRef.current = selectedTemplateId;
      setNotes(processedNotes);

      stopStream();
      setSuccessAlert(true);

      const isUnregistered = String(patientId) === "1";
      if (!inline) {
        if (isUnregistered) {
          Navigate("/bookings");
        } else {
          onSaved?.();
          Navigate(`/patients/appointment/${patientId}/1`);
        }
      }
    } catch (e) {
      console.error("UpdateSession save failed", e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {successAlert && <Alert variant="success">Session updated successfully!</Alert>}

      {/* APPT TEMPLATE */}
      {activeSection === "apptTemplate" && (
        <>
          <Row className="mb-3 mt-3">
            <Col md={6}>
              <Form.Group controlId="formGridSessionTemplate" className="d-flex align-items-center mb-3">
                <Form.Label className="me-3 mb-0 text-primary fw-bold">Template:</Form.Label>
                <Form.Select value={selectedTemplateId} onChange={(e) => handleTemplateChange(e.target.value)}>
                  <option value="" disabled>Choose a template...</option>
                  {dropdownOptions.map((category, idx) => (
                    <React.Fragment key={idx}>
                      <option disabled className="text-primary fw-bold">{category.label}</option>
                      {category.options.map((template) => (
                        <option key={template._id} value={template._id}>
                          {template.templateTitle}
                        </option>
                      ))}
                    </React.Fragment>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            {notes.map((note, idx) => {
              const rowKey = `${sessionId || "session"}-${note.sessionsSection || "sec"}-${idx}`;

              const safeGetFieldValue = (n) => {
                if (typeof getFieldValue === "function") return getFieldValue(n);
                if (!n || n.sessionsFieldType === "multiselect") return "";
                const v = n.sessionsSectionNotes;
                return typeof v === "string" ? v : (v ?? "") + "";
              };

              const msAvailable =
                (note.sessionsFieldType === "multiselect" &&
                  note.sessionsSectionNotes?.availableOptions) ||
                [];
              const msSelected =
                (note.sessionsFieldType === "multiselect" &&
                  note.sessionsSectionNotes?.selectedOptions) ||
                [];

              return (
                <Col md={6} key={rowKey} className="mb-3">
                  <div className="border p-3 rounded shadow-sm bg-white">
                    <Form.Label className="text-primary fw-bold d-flex justify-content-between align-items-center">
                      <span className="me-2">{note.sessionsSection || "Field"}</span>
                      {["text", "textarea"].includes(note.sessionsFieldType) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openRecorderForTemplate(idx);
                          }}
                          title="Speak to fill this field"
                        >
                          <FaMicrophone />
                        </Button>
                      )}
                    </Form.Label>

                    {note.sessionsFieldType === "text" && (
                      <Form.Control
                        type="text"
                        value={safeGetFieldValue(note)}
                        onChange={(e) => handleTemplateFieldChange(idx, e.target.value)}
                      />
                    )}

                    {note.sessionsFieldType === "textarea" && (
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={safeGetFieldValue(note)}
                        onChange={(e) => handleTemplateFieldChange(idx, e.target.value)}
                      />
                    )}

                    {note.sessionsFieldType === "multiselect" && msAvailable.length > 0 && (
                      <div className="mt-2">
                        {msAvailable.map((option, i) => (
                          <Form.Check
                            key={`${rowKey}-opt-${i}`}
                            label={option}
                            type="checkbox"
                            checked={msSelected.includes(option)}
                            onChange={() => handleMultiselectChange(idx, option)}
                          />
                        ))}
                      </div>
                    )}

                    {note.sessionsFieldType === "image" && (
                      <div className="mt-2">
                        <div className="annotator-scroll">
                          <ImageAnnotator
                            key={`${rowKey}-img`}
                            ref={(el) => (annotatorRefs.current[idx] = el)}
                            imageUrl={note.sessionsImageURL}
                            onUpdateImage={(newUrl) => {
                              setNotes((prev) => {
                                const updated = [...prev];
                                updated[idx] = { ...updated[idx], sessionsImageURL: newUrl };
                                return updated;
                              });
                            }}
                          />
                        </div>
                        <div className="mt-2 d-flex gap-2 justify-content-center">
                          <Button
                            className="rounded-0"
                            variant="outline-primary"
                            size="sm"
                            onClick={() => commitOneImage(idx)}
                          >
                            Apply edits
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </>
      )}

      {/* NOTEPAD */}
      {activeSection === "apptNotepad" && (
        <div className="mt-3">
          <Row className="align-items-center mb-3">
            <Col md={12}>
              <h5 className="text-primary fw-bold mb-0">Quick Notes</h5>
              <div className="d-flex align-items-center gap-2">
                <p className="text-muted mb-0 mt-1">Use this space for quick notes during the session.</p>
                <Button
                  variant="outline-primary"
                  className="ms-1 fs-6 border-0"
                  aria-label="Speak to add to notepad"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openRecorderForNotepad();
                  }}
                  title="Speak to add to notepad"
                >
                  <FaMicrophone />
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <div className="bg-white rounded shadow-sm right-scroll notepad-container">
                <ReactQuill
                  key={`quill-${sessionId}`}
                  ref={quillRef}
                  theme="snow"
                  readOnly={false}
                  value={notepadContent}
                  onChange={(html) => setNotepadContent(html)}
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link"],
                    ],
                  }}
                  formats={["bold", "italic", "underline", "list", "bullet", "link"]}
                  style={{ minHeight: "200px", height: "300px" }}
                  placeholder="Start typing your notes here..."
                />
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* MEDIA */}
      {activeSection === "apptMedia" && (
        <div className="mt-3">
          <Row className="align-items-center mb-3">
            <Col md={12}>
              <h5 className="text-primary fw-bold mb-2">Capture Photos</h5>
              <p className="text-muted mb-0">Take additional photos or review existing ones for this session.</p>
            </Col>
          </Row>

          {camError ? (
            <Alert variant="warning" className="mb-3">
              Camera access failed ({camError}). You can still upload from gallery:
              <Form.Control
                className="mt-2"
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((file) => {
                    const reader = new FileReader();
                    reader.onload = () => setMediaImages((prev) => [reader.result, ...prev]);
                    reader.readAsDataURL(file);
                  });
                  e.target.value = "";
                }}
              />
            </Alert>
          ) : (
            <div className="rounded right-scroll shadow-sm mb-3" style={{ position: "relative" }}>
              <video ref={videoRef} playsInline muted style={{ width: "100%", height: "auto", background: "#000" }} />
              <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center gap-3 p-3">
                <Button variant="light" className="rounded-circle" onClick={capturePhoto} aria-label="Capture">
                  <i className="bi bi-camera-fill" />
                </Button>
                <Button variant="light" className="rounded-circle" onClick={flipCamera} aria-label="Flip">
                  <i className="bi bi-arrow-repeat" />
                </Button>
              </div>
            </div>
          )}

          {mediaImages.length > 0 && (
            <>
              <h6 className="text-primary">Photos ({mediaImages.length})</h6>
              <Row>
                {mediaImages.map((url, idx) => (
                  <Col xs={4} sm={3} md={2} key={idx} className="mb-3">
                    <div className="position-relative border rounded right-scroll">
                      <img src={url} alt={`photo ${idx + 1}`} style={{ width: "100%", height: 100, objectFit: "cover" }} />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1 p-1"
                        onClick={() => removePhoto(idx)}
                        aria-label="Remove"
                        title="Remove"
                      >
                        ×
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </div>
      )}

      {/* SAVE */}
      {["apptTemplate", "apptNotepad", "apptMedia"].includes(activeSection) && (
        <div className="d-flex justify-content-center mt-4">
          <Button variant="primary" className="rounded-0" size="lg" onClick={handleSubmit} disabled={isSaving}>
            <i className="fa-regular fa-floppy-disk me-2"></i>
            {isSaving ? "Saving..." : "Save Session"}
          </Button>
        </div>
      )}

      <RecorderModal show={recOpen} onClose={closeRecorder} onDone={applyTranscriptToTarget} vr={vr} />
    </div>
  );
};
